import { Component } from '@theme/component';
import { addSelectionToWishlist, getWishlistIds } from '@theme/wishlist';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { parseBudget, validPreferences, validQuantity, candidatesFor, conflictsWith, composeSelection, restoreSelection, selectionTotal, selectionSignature, unitPrice } from '@theme/assortment-engine';

const STORAGE = 'esotericae:assortment:v1';
const WEEK = 7 * 24 * 60 * 60 * 1000;

/** @extends {Component<{form: HTMLFormElement, results: HTMLElement, resultsHeading: HTMLElement, cards: HTMLElement, status: HTMLElement, printArea: HTMLElement, addButton?: HTMLButtonElement, wishlistButton: HTMLButtonElement, printButton: HTMLButtonElement, composeButton: HTMLButtonElement, catalogueStatus: HTMLElement, replacementDialog: HTMLDialogElement, replacementSearch: HTMLInputElement, replacementList: HTMLElement}>} */
class AssortmentComposer extends Component {
  requiredRefs = ['form', 'results', 'resultsHeading', 'cards', 'status', 'printArea', 'printButton', 'wishlistButton', 'composeButton', 'catalogueStatus', 'replacementDialog', 'replacementSearch', 'replacementList'];
  rows = [];
  preferences = null;
  budget = null;
  busy = false;
  wishlistBusy = false;
  dirty = false;
  attempt = null;
  controller = null;
  config = null;
  storageUnavailable = false;
  loading = true;
  added = {};
  replacementId = null;

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.removeAttribute('data-printing');
    super.disconnectedCallback();
  }

  async initialize() {
    this.controller?.abort();
    this.wishlistBusy = false;
    this.controller = new AbortController();
    const controller = this.controller;
    this.config = this.readPage(this);
    this.loading = true;
    this.refs.cards.inert = true;
    this.refs.composeButton.disabled = true;
    this.refs.catalogueStatus.hidden = false;
    this.refs.catalogueStatus.textContent = this.text('catalogue_loading');
    this.querySelector('[data-retry]').hidden = true;
    document.addEventListener('wishlist:change', () => this.renderWishlistSummary(), { signal: this.controller.signal });
    window.addEventListener('afterprint', () => this.removeAttribute('data-printing'), { signal: this.controller.signal });
    try {
      const config = await this.completeCatalogue(this, this.config, controller.signal);
      if (controller.signal.aborted || this.controller !== controller) return;
      this.config = config;
      this.loading = false;
      this.refs.cards.inert = false;
      this.refs.composeButton.disabled = false;
      this.refs.catalogueStatus.textContent = '';
      this.refs.catalogueStatus.hidden = true;
      this.restore();
      this.updateLogin();
    } catch {
      if (controller.signal.aborted || this.controller !== controller) return;
      this.refs.catalogueStatus.textContent = this.text('catalogue_error');
      this.querySelector('[data-retry]').hidden = false;
      this.announce('catalogue_error');
    }
  }

  readPage(element) {
    const config = JSON.parse(element.querySelector('[data-config]').textContent);
    config.products = [...element.querySelectorAll('script[data-candidate]')].map((script) => JSON.parse(script.textContent));
    return config;
  }

  async completeCatalogue(element, config, signal = this.controller.signal) {
    const products = new Map(config.products.map((product) => [product.id, product]));
    for (let page = 1; page <= config.pages; page++) {
      if (page === config.page) continue;
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set(config.pageParam, String(page));
      const response = await fetch(url, { cache: 'no-store', signal });
      if (!response.ok) throw new Error('Catalogue page unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = document.querySelector('assortment-composer');
      if (!next) throw new Error('Catalogue page missing');
      const chunk = this.readPage(next);
      if (chunk.priced !== config.priced || chunk.currency !== config.currency || chunk.page !== page || chunk.catalogueTotal !== config.catalogueTotal) throw new Error('Catalogue context changed');
      for (const product of chunk.products) products.set(product.id, product);
      for (const template of next.querySelectorAll('template[data-candidate-id]')) {
        if (!element.querySelector(`template[data-candidate-id="${template.dataset.candidateId}"]`)) element.append(template.cloneNode(true));
      }
    }
    config.products = [...products.values()];
    if (new Set(config.products.map((product) => product.productId)).size !== config.catalogueTotal) throw new Error('Incomplete catalogue');
    return config;
  }

  normalizeAdded(config) {
    for (const product of config.products) product.inCart = Math.max(0, (product.inCart || 0) - (this.added[product.id] || 0));
    return config;
  }

  text(key, values = {}) {
    let value = this.config.labels[key] || '';
    for (const [name, replacement] of Object.entries(values)) value = value.replaceAll(`[${name}]`, String(replacement));
    return value;
  }

  money(cents, currency = this.config.currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency }).format(cents / 100);
  }

  announce(key, values) {
    this.refs.status.textContent = this.text(key, values);
  }

  readPreferences() {
    const data = new FormData(this.refs.form);
    return { goal: data.get('goal'), profile: data.get('profile'), families: data.getAll('family') };
  }

  /** @param {SubmitEvent} event */
  compose(event) {
    event.preventDefault();
    if (this.busy || this.wishlistBusy || this.loading) return;
    const preferences = this.readPreferences();
    const budgetInput = this.refs.form.elements.namedItem('budget');
    const budget = this.config.priced ? parseBudget(budgetInput?.value) : null;
    budgetInput?.removeAttribute('aria-invalid');
    if (!validPreferences(preferences)) {
      this.announce('family_required');
      this.refs.form.querySelector('[name=family]').focus();
      return;
    }
    if (this.config.priced && (budget === null || budget < this.config.minimum)) {
      budgetInput?.setAttribute('aria-invalid', 'true');
      this.announce('budget_error');
      budgetInput?.focus();
      return;
    }
    this.preferences = preferences;
    this.budget = budget;
    this.dirty = false;
    this.rows = composeSelection(this.config.products, preferences, this.config.priced ? budget : null).rows;
    for (const row of this.rows) if (this.added[row.id]) row.quantity = this.added[row.id];
    this.render();
    this.save();
    this.announce(this.rows.length ? 'selection_ready' : 'selection_empty', { count: this.rows.length });
    this.refs.resultsHeading.focus();
  }

  /** @param {Event} event */
  preferencesChanged(event) {
    const target = event.composedPath()[0];
    if (target instanceof HTMLSelectElement && target.name === 'profile') {
      const defaults = { general: ['encens', 'resines', 'plantes'], fumigation: ['encens', 'resines', 'plantes'], hoodoo: ['plantes', 'huiles', 'poudres'], santeria: ['plantes', 'poudres', 'accessoires'] };
      for (const input of this.refs.form.querySelectorAll('[name=family]')) input.checked = defaults[target.value].includes(input.value);
    }
    if (this.preferences) {
      this.dirty = true;
      this.renderSummary();
      this.announce('choices_changed');
    }
  }

  reset() {
    if (this.busy || this.wishlistBusy) return;
    this.refs.form.reset();
    this.rows = [];
    this.preferences = null;
    this.budget = null;
    this.dirty = false;
    this.attempt = null;
    for (const product of this.config.products) product.inCart += this.added[product.id] || 0;
    this.added = {};
    this.refs.cards.replaceChildren();
    this.refs.results.hidden = true;
    try { localStorage.removeItem(STORAGE); } catch { /* Storage is optional. */ }
    this.refs.form.querySelector('select').focus();
    this.announce('ready');
  }

  render(focusId) {
    this.refs.results.hidden = false;
    const fragment = document.createDocumentFragment();
    for (const row of this.rows) {
      const product = this.config.products.find((item) => item.id === row.id);
      const template = this.querySelector(`template[data-candidate-id="${row.id}"]`);
      if (!(template instanceof HTMLTemplateElement) || !product) continue;
      const card = template.content.firstElementChild.cloneNode(true);
      card.querySelector('[data-lot]').textContent = this.text('lot', { count: product.min });
      const input = card.querySelector('[data-quantity]');
      if (input instanceof HTMLInputElement) {
        input.value = row.quantity;
        input.disabled = Boolean(this.added[row.id]);
        input.min = product.min;
        input.step = product.step;
        if (product.max !== null) input.max = Math.max(0, product.max - product.inCart);
        const valid = validQuantity(product, row.quantity, product.inCart || 0);
        input.setAttribute('aria-invalid', String(!valid));
        let rules = this.text('quantity_help', { min: product.min, step: product.step });
        if (product.max !== null) rules += ` ${this.text('maximum', { count: product.max })}`;
        card.querySelector('[data-rules]').textContent = rules;
        const price = unitPrice(product, row.quantity);
        card.querySelector('[data-unit-price]').textContent = `${this.money(price)} ${this.text('unit_price')}`;
        card.querySelector('[data-line-total]').textContent = this.money(price * row.quantity);
      }
      for (const button of card.querySelectorAll('button[data-action]')) button.disabled = this.busy || button.dataset.action === 'add' && Boolean(this.added[row.id]);
      const addLabel = card.querySelector('[data-add-label]');
      if (addLabel && this.added[row.id]) {
        addLabel.textContent = this.text('added_short');
        const button = addLabel.closest('button'); button.title = this.text('added_short'); button.dataset.inCart = 'true';
      }
      fragment.append(card);
    }
    this.refs.cards.replaceChildren(fragment);
    this.querySelector('[data-wishlist-feedback]').hidden = true;
    this.renderSummary();
    this.updateLogin();
    if (focusId) {
      const card = [...this.refs.cards.children].find((item) => item.dataset.id === focusId);
      (card?.querySelector('button') || this.refs.resultsHeading).focus();
    }
  }

  renderSummary() {
    if (!this.preferences) return;
    const products = this.config.products;
    const families = [...new Set(this.rows.map((row) => products.find((item) => item.id === row.id)?.family))];
    this.querySelector('[data-count]').textContent = `${this.rows.length} ${this.text('references')} · ${families.length} ${this.text('families_count')}`;
    const total = this.config.priced ? selectionTotal(this.rows, products) : null;
    const summary = this.querySelector('[data-summary]');
    summary.replaceChildren();
    summary.hidden = !this.config.priced;
    const entries = this.config.priced ? [['selected_budget', this.money(this.budget, this.config.budgetCurrency)]] : [];
    if (this.config.priced && total !== null) entries.push(['selected_total', this.money(total)], ['remaining', this.money(this.budget - total)]);
    for (const [key, value] of entries) {
      const dl = document.createElement('dl');
      const dt = document.createElement('dt'); dt.textContent = this.text(key);
      const dd = document.createElement('dd'); dd.textContent = value;
      dl.append(dt, dd); summary.append(dl);
    }
    const warnings = [];
    const missing = this.preferences.families.filter((family) => !families.includes(family));
    if (!this.rows.length) warnings.push(this.text('selection_cleared'));
    if (missing.length) warnings.push(this.text('missing_families', { families: missing.map((family) => this.config.families[family]).join(', ') }));
    if (this.config.priced && total === null) warnings.push(this.text('quantity_error'));
    if (total > this.budget) warnings.push(this.text('over_budget', { amount: this.money(total - this.budget) }));
    const pending = this.rows.filter((row) => !this.added[row.id]);
    const pendingTotal = this.config.priced ? selectionTotal(pending, products) : null;
    const under = this.config.priced && pendingTotal !== null && pendingTotal + this.config.cartTotal < this.config.minimum;
    if (under) warnings.push(this.text('under_minimum', { amount: this.money(this.config.minimum - pendingTotal - this.config.cartTotal) }));
    if (this.dirty) warnings.push(this.text('choices_changed'));
    const warning = this.querySelector('[data-warning]');
    warning.textContent = warnings.join(' '); warning.hidden = !warnings.length;
    const uncertain = this.attempt?.status === 'checking';
    const attempted = !pending.length;
    if (this.refs.addButton) {
      this.refs.addButton.disabled = this.busy || this.wishlistBusy || this.dirty || !this.rows.length || total === null || total > this.budget || under || attempted || uncertain;
      this.refs.addButton.querySelector('[data-bulk-cart-label]').textContent = this.text(this.busy ? 'adding' : !pending.length ? 'added' : Object.keys(this.added).length ? 'add_remaining' : 'add_cart');
    }
    for (const card of this.refs.cards.querySelectorAll('[data-id]')) {
      for (const action of card.querySelectorAll('[data-action=replace], [data-action=remove]')) action.disabled = this.busy || this.wishlistBusy || Boolean(this.added[card.dataset.id]);
      const button = card.querySelector('[data-action=add]');
      const row = this.rows.find((item) => item.id === card.dataset.id);
      const product = products.find((item) => item.id === card.dataset.id);
      if (button) button.disabled = this.busy || this.wishlistBusy || this.dirty || uncertain || Boolean(this.added[card.dataset.id]) || !validQuantity(product, row.quantity, product.inCart || 0) || total === null || total > this.budget;
    }
    this.refs.printButton.disabled = !this.rows.length || total === null && this.config.priced || this.busy || this.wishlistBusy;
    this.renderWishlistSummary();
    this.querySelector('[data-cart-link]').hidden = !this.attempt;
    this.querySelector('[data-minimum]').textContent = this.text('minimum_note', { amount: this.money(this.config.minimum, this.config.budgetCurrency) });
  }

  renderWishlistSummary() {
    if (!this.config) return;
    const ids = getWishlistIds();
    const complete = this.rows.length > 0 && this.rows.every((row) => ids.has(row.id));
    const button = this.refs.wishlistButton;
    button.disabled = this.busy || this.wishlistBusy || this.loading || this.dirty || !this.rows.length || complete;
    button.querySelector('[data-bulk-wishlist-label]').textContent = this.text(this.wishlistBusy ? 'adding_wishlist' : complete ? 'wishlist_complete' : 'add_wishlist');
    button.setAttribute('aria-busy', String(this.wishlistBusy));
  }

  async addSelectionToWishlist() {
    if (this.busy || this.wishlistBusy || this.loading || this.dirty || !this.rows.length) return;
    const controller = this.controller;
    this.wishlistBusy = true;
    this.refs.cards.inert = true; this.refs.form.inert = true; this.renderSummary();
    const feedback = this.querySelector('[data-wishlist-feedback]');
    feedback.hidden = true;
    let message;
    try {
      const result = await addSelectionToWishlist([...this.refs.cards.querySelectorAll('wishlist-button')], controller.signal);
      const ids = getWishlistIds();
      const count = this.rows.filter((row) => ids.has(row.id)).length;
      const key = result.loginRequired ? 'wishlist_login' : result.full ? 'wishlist_full' : result.failed.length ? 'wishlist_partial' : 'wishlist_success';
      message = this.text(key, { count, total: this.rows.length });
    } catch { message = this.text('wishlist_error'); }
    finally {
      if (this.controller === controller) this.wishlistBusy = false;
      if (this.controller === controller && !controller.signal.aborted) {
        this.refs.cards.inert = false; this.refs.form.inert = false;
        this.renderSummary();
        feedback.textContent = message; feedback.hidden = false;
        this.refs.status.textContent = message;
      }
    }
  }

  /** @param {MouseEvent} event */
  cardAction(event) {
    const target = event.composedPath()[0];
    const button = target instanceof Element ? target.closest('button[data-action]') : null;
    const card = button?.closest('[data-id]');
    if (!card || this.busy || this.wishlistBusy || this.loading) return;
    const index = this.rows.findIndex((row) => row.id === card.dataset.id);
    if (index < 0) return;
    if (button.dataset.action === 'add') {
      this.addToCart(undefined, card.dataset.id);
      return;
    }
    if (button.dataset.action === 'remove') {
      this.rows.splice(index, 1);
      this.render(this.rows[Math.min(index, this.rows.length - 1)]?.id);
      if (!this.rows.length) this.refs.resultsHeading.focus();
      this.save(); this.announce('removed');
      return;
    }
    this.replacementId = card.dataset.id;
    this.refs.replacementSearch.value = '';
    this.searchReplacements();
    this.refs.replacementDialog.showModal();
    this.refs.replacementSearch.focus();
  }

  searchReplacements() {
    const current = this.config.products.find((item) => item.id === this.replacementId);
    if (!current) return;
    const query = this.refs.replacementSearch.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const others = this.rows.filter((row) => row.id !== current.id);
    const pool = candidatesFor(this.config.products, this.preferences).filter((item) => item.family === current.family && item.id !== current.id && !this.added[item.id] && !conflictsWith(item, others, this.config.products));
    const fragment = document.createDocumentFragment();
    for (const product of pool) {
      if (!`${product.title} ${product.vendor}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(query)) continue;
      const rows = [...others, { id: product.id, quantity: product.min }];
      const total = this.config.priced ? selectionTotal(rows, this.config.products) : 0;
      if (this.config.priced && (total === null || total > this.budget)) continue;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'as__replacement-option'; button.dataset.replaceId = product.id;
      const text = document.createElement('span'); text.textContent = product.title;
      const detail = document.createElement('small'); detail.textContent = `${product.vendor} · ${this.text('lot', { count: product.min })}`; text.append(detail);
      const action = document.createElement('span'); action.textContent = this.text('choose'); button.append(text, action); fragment.append(button);
    }
    if (!fragment.childNodes.length) { const empty = document.createElement('p'); empty.textContent = this.text('replacement_empty'); fragment.append(empty); }
    this.refs.replacementList.replaceChildren(fragment);
  }

  /** @param {MouseEvent} event */
  chooseReplacement(event) {
    const target = event.composedPath()[0];
    const button = target instanceof Element ? target.closest('[data-replace-id]') : null;
    if (!button) return;
    const product = this.config.products.find((item) => item.id === button.dataset.replaceId);
    const index = this.rows.findIndex((row) => row.id === this.replacementId);
    if (!product || index < 0) return;
    this.rows[index] = { id: product.id, quantity: product.min };
    this.refs.replacementDialog.close(); this.replacementId = null;
    this.render(product.id); this.save(); this.announce('replaced');
  }

  closeReplacement(event) {
    event?.preventDefault();
    const id = this.replacementId;
    this.refs.replacementDialog.close(); this.replacementId = null;
    this.refs.cards.querySelector(`[data-id="${id}"] [data-action=replace]`)?.focus();
  }

  /** @param {Event} event */
  quantityChanged(event) {
    const input = event.composedPath()[0];
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-quantity]') || this.busy || this.wishlistBusy || this.loading) return;
    const id = input.closest('[data-id]')?.dataset.id;
    const row = this.rows.find((item) => item.id === id);
    const product = this.config.products.find((item) => item.id === id);
    if (!row || !product) return;
    row.quantity = Number(input.value);
    const valid = validQuantity(product, row.quantity, product.inCart || 0);
    input.setAttribute('aria-invalid', String(!valid));
    const price = unitPrice(product, row.quantity);
    input.closest('[data-id]').querySelector('[data-line-total]').textContent = valid ? this.money(price * row.quantity) : '—';
    input.closest('[data-id]').querySelector('[data-unit-price]').textContent = `${this.money(price)} ${this.text('unit_price')}`;
    this.renderSummary(); this.save();
    if (!valid) this.announce('quantity_error');
  }

  save() {
    if (!this.preferences) return;
    try {
      localStorage.setItem(STORAGE, JSON.stringify({ version: 1, savedAt: Date.now(), priced: this.config.priced, preferences: this.preferences, budget: this.budget, rows: this.rows, attempt: this.attempt, added: this.added }));
    } catch {
      if (!this.storageUnavailable) { this.storageUnavailable = true; this.announce('storage_unavailable'); }
    }
  }

  restore() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE) || 'null');
      if (!saved || saved.version !== 1 || !Number.isSafeInteger(saved.savedAt) || Date.now() - saved.savedAt > WEEK || !validPreferences(saved.preferences)) return;
      const savedBudgetValid = Number.isSafeInteger(saved.budget) && saved.budget >= this.config.minimum && saved.budget <= 10000000;
      if (saved.priced && !savedBudgetValid) return;
      const budgetInput = this.refs.form.elements.namedItem('budget');
      if (this.config.priced && !budgetInput) return;
      const defaultBudget = this.config.priced ? Math.max(parseBudget(budgetInput.value) || this.config.minimum, this.config.minimum) : null;
      this.preferences = saved.preferences;
      this.budget = this.config.priced ? (savedBudgetValid ? saved.budget : defaultBudget) : null;
      this.attempt = saved.attempt;
      this.added = {};
      for (const product of this.config.products) {
        const quantity = saved.added?.[product.id];
        if (Number.isSafeInteger(quantity) && quantity > 0 && product.inCart >= quantity) this.added[product.id] = quantity;
      }
      if (this.attempt?.status === 'success' && !Object.keys(this.added).length) this.attempt = null;
      this.normalizeAdded(this.config);
      this.rows = restoreSelection(saved.rows, this.config.products, this.preferences);
      this.refs.form.elements.namedItem('goal').value = this.preferences.goal;
      this.refs.form.elements.namedItem('profile').value = this.preferences.profile;
      if (budgetInput && this.config.priced) budgetInput.value = (this.budget / 100).toString().replace('.', ',');
      for (const input of this.refs.form.querySelectorAll('[name=family]')) input.checked = this.preferences.families.includes(input.value);
      if (this.config.priced && !this.attempt && (!saved.priced || selectionTotal(this.rows, this.config.products) > this.budget)) {
        const selected = new Set(this.rows.map((row) => row.id));
        this.rows = composeSelection(this.config.products.filter((product) => selected.has(product.id)), this.preferences, this.budget).rows;
      }
      this.dirty = false;
      this.render();
      this.announce(selectionSignature(this.rows) === selectionSignature(saved.rows || []) ? 'saved' : 'restored_changed');
      this.save();
    } catch { /* Invalid or unavailable local storage cannot block the composer. */ }
  }

  updateLogin() {
    const url = new URL('/customer_authentication/login', location.origin);
    url.searchParams.set('return_to', `${location.pathname}${location.search}${location.hash}`);
    for (const link of this.querySelectorAll('[data-login]')) if (link instanceof HTMLAnchorElement) link.href = url.href;
  }

  saveBeforeLogin() { this.save(); }

  async freshConfiguration() {
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set(this.config.pageParam || 'page', '1');
    const response = await fetch(url, { cache: 'no-store', signal: this.controller.signal });
    if (!response.ok) throw new Error('Configuration unavailable');
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const element = document.querySelector('assortment-composer');
    if (!element) throw new Error('Missing configuration');
    return { config: this.normalizeAdded(await this.completeCatalogue(element, this.readPage(element))), element };
  }

  fingerprint(config) {
    return JSON.stringify(this.rows.map((row) => {
      const p = config.products.find((item) => item.id === row.id);
      return p ? [p.id, p.available, p.min, p.step, p.max, p.inCart, p.price, p.breaks] : null;
    }));
  }

  async addToCart(event, targetId = null) {
    if (this.busy || this.wishlistBusy || this.loading || !this.config.priced || this.dirty || this.attempt?.status === 'checking' || !targetId && this.refs.addButton?.disabled) return;
    const selected = this.rows.filter((row) => !this.added[row.id] && (!targetId || row.id === targetId));
    if (!selected.length) return;
    this.busy = true; this.refs.cards.inert = true; this.refs.form.inert = true; this.renderSummary();
    let deferred;
    let posted = false;
    try {
      const fresh = await this.freshConfiguration();
      if (!fresh.config.priced || fresh.config.currency !== this.config.currency) {
        this.announce('account_changed');
        this.refs.addButton.disabled = true;
        this.dirty = true;
        return;
      }
      const changed = this.fingerprint(fresh.config) !== this.fingerprint(this.config);
      this.config = fresh.config;
      for (const template of fresh.element.querySelectorAll('template[data-candidate-id]')) {
        const existing = this.querySelector(`template[data-candidate-id="${template.dataset.candidateId}"]`);
        if (existing) existing.replaceWith(template.cloneNode(true));
      }
      const restored = restoreSelection(this.rows, this.config.products, this.preferences);
      if (changed || selectionSignature(restored) !== selectionSignature(this.rows)) {
        this.rows = restored; this.render(); this.save(); this.announce('cart_changed');
        return;
      }
      const root = window.Shopify?.routes?.root || '/';
      const catalogueFingerprint = this.fingerprint(this.config);
      const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store', signal: this.controller.signal });
      if (!cartResponse.ok) throw new Error('Cart unavailable');
      const before = await cartResponse.json();
      if (before.currency !== this.config.currency) throw new Error('Currency changed');
      for (const product of this.config.products) product.inCart = Math.max(0, before.items.filter((item) => String(item.variant_id) === product.id).reduce((sum, item) => sum + item.quantity, 0) - (this.added[product.id] || 0));
      this.config.cartTotal = before.total_price;
      const total = selectionTotal(this.rows, this.config.products);
      const pendingTotal = selectionTotal(selected, this.config.products);
      if (total === null || total > this.budget || !targetId && pendingTotal + before.total_price < this.config.minimum || this.fingerprint(this.config) !== catalogueFingerprint) {
        this.render(); this.announce('cart_changed'); return;
      }
      const signature = selectionSignature(selected);
      if (this.attempt?.signature === signature && this.attempt.status === 'checking') return;
      const items = selected.map((row) => ({ id: Number(row.id), quantity: row.quantity }));
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
      this.attempt = { signature, status: 'checking' }; this.save();
      posted = true;
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: location.pathname }), signal: this.controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error('Cart addition not confirmed');
      const afterResponse = await fetch(`${root}cart.js`, { cache: 'no-store', signal: this.controller.signal });
      if (!afterResponse.ok) throw new Error('Cart confirmation unavailable');
      const cart = await afterResponse.json();
      const confirmed = items.every((item) => {
        const previous = before.items.filter((line) => Number(line.variant_id) === item.id).reduce((sum, line) => sum + line.quantity, 0);
        const current = cart.items.filter((line) => Number(line.variant_id) === item.id).reduce((sum, line) => sum + line.quantity, 0);
        return current >= previous + item.quantity;
      });
      if (!confirmed) throw new Error('Cart quantities not confirmed');
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'assortment-composer', sourceId: this.id, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), sections: result.sections, didError: false } });
      for (const row of selected) this.added[row.id] = row.quantity;
      for (const product of this.config.products) product.inCart = Math.max(0, cart.items.filter((item) => String(item.variant_id) === product.id).reduce((sum, item) => sum + item.quantity, 0) - (this.added[product.id] || 0));
      this.config.cartTotal = cart.total_price;
      this.attempt = { signature, status: 'success' }; this.render(); this.save(); this.announce(targetId ? 'added_one' : 'cart_success');
    } catch (error) {
      if (posted) {
        deferred?.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: this.text('cart_error'), code: 'INVALID' }));
        this.announce('cart_error');
      } else this.announce('verification_error');
    } finally {
      this.busy = false;
      this.refs.cards.inert = false; this.refs.form.inert = false;
      this.renderSummary();
    }
  }

  printList() {
    if (!this.rows.length || this.busy || this.wishlistBusy) return;
    const area = this.refs.printArea; area.replaceChildren();
    const append = (tag, value, parent = area) => { const element = document.createElement(tag); element.textContent = value; parent.append(element); return element; };
    append('h2', this.text('print_title'));
    append('p', `Esotericæ · ${new Intl.DateTimeFormat(document.documentElement.lang || 'fr').format(new Date())}`);
    const project = `${this.text(`goal_${this.preferences.goal}`)} · ${this.text(`profile_${this.preferences.profile}`)}`;
    append('p', this.config.priced ? `${project} · ${this.text('selected_budget')} : ${this.money(this.budget, this.config.budgetCurrency)}` : project);
    if (!this.config.priced) append('p', this.text('anonymous_note'));
    const table = document.createElement('table');
    const head = table.createTHead().insertRow();
    for (const label of [this.text('references'), this.config.priced ? this.text('quantity') : this.text('qualitative_quantity'), ...(this.config.priced ? [this.text('selected_total')] : [])]) append('th', label, head).scope = 'col';
    const body = table.createTBody();
    for (const row of this.rows) {
      const product = this.config.products.find((item) => item.id === row.id);
      const tr = body.insertRow();
      append('td', `${product.title} — ${product.vendor}`, tr);
      append('td', String(this.config.priced ? row.quantity : product.min), tr);
      if (this.config.priced) append('td', this.money(unitPrice(product, row.quantity) * row.quantity), tr);
    }
    area.append(table);
    if (this.config.priced) append('p', `${this.text('selected_total')} : ${this.money(selectionTotal(this.rows, this.config.products))}`);
    append('p', this.text('print_note'));
    this.setAttribute('data-printing', '');
    window.print();
  }
}

if (!customElements.get('assortment-composer')) customElements.define('assortment-composer', AssortmentComposer);
